import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { css } from '@emotion/react'
import RichText from '~components/RichText'
import Seo from '~components/Seo'
import Section from '~components/Section'
import Image from '~components/Image'
import DataViewer from '~utils/DataViewer'
import { mobile } from '~styles/global'
import styled from '@emotion/styled'
import ModuleStyles from '~styles/ModuleStyles'
import {
	FacebookShareButton,
	TwitterShareButton,
	LinkedinShareButton,
} from 'react-share'
import TileArticle from '~components/TileArticle'
import TileProduct from '~components/TileProduct'
import { JNLetters } from '../components/Svg'
import { AnimatePresence, motion } from 'framer-motion'

const ShareLinksBlock = ({className, title}) => {
	const [currentUrl, setCurrentUrl] = useState(null)
	const [copied, setCopied] = useState(false)

	useEffect(()=> {
		setCurrentUrl(window.location.href)
	}, [])

	const copyLink = () => {
		navigator.clipboard.writeText(currentUrl)
		setCopied(true)
		setTimeout(()=> {
			setCopied(false)
		}, 3000)
	}
	
	return (
		<ShareLinks className={className}>
			<ShareLink>
				<TwitterShareButton url={currentUrl}>
					<h4>
						Twitter 
					</h4>
				</TwitterShareButton>
			</ShareLink>
			<ShareLink>
				<button>
					<h4>
						<a href={'mailto:?subject=' + title + '&body=' + currentUrl}>
						Email
						</a>
					</h4> 	
				</button>
			</ShareLink>
			<ShareLink css={css`
			`}>
				<button 
					onClick={() => copyLink()} 	
					css={css`${mobile}{
						display: none;
				}`}> 
					<h4>
						{copied ? 'Copied' : 'Link'}
					</h4>
				</button>
				<button 
					onClick={() => copyLink()} 	
					css={css`
					display: none;
					${mobile}{
						display: inline-block;
				}`}> 
					<h4>
						{copied ? 'Copied' : 'Link'}
					</h4>
				</button>
			</ShareLink>
			<ShareLink>
				<LinkedinShareButton url={currentUrl}>
					<h4>
						LinkedIn 
					</h4>
				</LinkedinShareButton>
			</ShareLink>
			<ShareLink>
				<FacebookShareButton url={currentUrl}>
					<h4>
						Facebook 
					</h4>
				</FacebookShareButton>
			</ShareLink>
		</ShareLinks>
	)
}

const Article = ({ data }) => {
	const { title, content, datePublished } = data.sanityArticle
	
	return(
		<>
			<Seo 
				title={title}
				metaTitle={content?.seo?.metaTitle}
				description={content?.seo?.metaDescription}
				image={content?.seo?.socialImage}
			/>
			<DataViewer data={content} name="content"/>
			<StickyWrap>
				<StickyBlock css={css`
					${mobile}{
						display: none;
				}`} >
					<ShareLinksBlock title={title}/>
				</StickyBlock>
				<Header>
					<PreHeading>Journal / {content.topics.map(i=>(i.title)).join(', ')} ● <span className='numbers'>{datePublished}</span></PreHeading>
					<Heading className='large'>{title}</Heading>
					<HeaderImage 
						image={content?.headerImage} 
						css={css`
						grid-column: ${content?.headerImage?.asset?.metadata?.dimensions?.aspectRatio < 1 ? '5/9' : '3/11'};
						padding: ${content?.headerImage?.asset?.metadata?.dimensions?.aspectRatio < 1 ? '0 20px' : '0 60px'};
						${mobile}{
							grid-column: ${content?.headerImage?.asset?.metadata?.dimensions?.aspectRatio < 1 ? '2/6' : '1/7'};
						}
					`}/>
					<ShareLinksBlock  
						css={css`
							display: none; 
							${mobile}{
								display: flex;
						}`} 
						title={title}
					/>
				</Header>
				<ModuleStyles>
					<RichText content={content.text}/>
				</ModuleStyles>
			</StickyWrap>
			<Related> 
				<RelatedBox css={css`${mobile}{
					display: ${content.relatedArticles?.length === 0 ? 'none' : 'block'};;
				}`}>
					<RelatedHeading>
						Keep Reading
					</RelatedHeading>
					<RelatedTiles>
						{content.relatedArticles?.map(article => (
							<RelatedArticle 
								article={article} 
								key={article.title}
							/>
						))}
						<JNBox 
							css={css`
								grid-column: ${content.relatedArticles?.length === 0 ? 'span 2' : 'span 1'};
								display: ${content.relatedArticles?.length < 2 ? 'block' : 'none'};
							`}>
							<JNLogo/>
						</JNBox>
					</RelatedTiles>
				</RelatedBox>
				<RelatedBox 
					css={css`${mobile}{
						display: ${content.relatedProducts?.length === 0 ? 'none' : 'block'};;
				}`}>	
					<RelatedHeading>
						Explore the edit
					</RelatedHeading>
					<RelatedTiles>
						{content.relatedProducts?.map(product => (
							<RelatedProduct
								product={product} 
								key={product?.title}
							/>
						))}
						<JNBox 
							css={css`
								grid-column: ${content.relatedProducts?.length === 0 ? 'span 2' : 'span 1'};
								display: ${content.relatedProducts?.length < 2 ? 'block' : 'none'};
							`}>
							<JNLogo/>
						</JNBox>
					</RelatedTiles>
				</RelatedBox>
			</Related>
		</>
	)
}
const StickyWrap = styled.div`
	position: relative;
`
const StickyBlock = styled.div`
	position: sticky;
	top: 100vh;
	z-index: 2;
	align-items: end;
	justify-content: start;
	display: flex;
	height: 0;
	width: 0;
`
const ShareLinks = styled.div`
	/* writing-mode: vertical-rl;
	text-orientation: mixed; */
	transform: rotate(270deg) translate(0px, 0px);
	transform-origin: top left;
	display: flex;
	border: 1px solid var(--black);
	box-sizing: border-box;
	position: absolute;
	left: 40px;
	bottom: 20px;
	${mobile}{
		position: relative;
		left: auto;
		bottom: auto;
		grid-column: span 6;
		writing-mode: horizontal-tb;
		text-orientation: normal;
		transform: none;
		justify-self: auto;
		display: flex;
		margin-bottom: var(--xxl)
	}
`
const ShareLink = styled.div`
	padding: 4px 12px 3px;
	border-right: 1px solid var(--black);
	background-color: var(--beige);
	text-align: center;
	display: grid;
	&:last-of-type{
		border-right: none;
	}
	${mobile}{
		padding: 4px 7px 3px;
		border-bottom: none;
		flex: 1;
	}
`
const Header = styled(Section)`
	padding-top: 120px;
	border-bottom: 1px solid var(--black);
	padding-bottom: 77px;
	${mobile}{
		padding-top: var(--xxxl);
		padding-bottom: 0px;
	}
`
const PreHeading = styled.h3`
	grid-column: 2/12;
	text-align: center;
	margin-bottom: 38px;
	${mobile}{
		grid-column: span 6;
		margin-bottom: 20px;
	}
`
const Heading = styled.h1`
	grid-column: 3/11;
	text-align: center;
	margin-bottom: 64px;
	${mobile}{
		grid-column: span 6;
		margin-bottom: 49px;
	}
`
const HeaderImage = styled(Image)`
	${mobile}{
		padding: 0px;
		margin-bottom: 60px;
	}
`
const Related = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	border-top: 1px solid var(--black);
	border-bottom: 1px solid var(--black);
	${mobile}{
		grid-template-columns: 1fr;
		border-bottom: none;
	}
`
const RelatedHeading = styled.h2`
	padding: 30px 20px;
	text-align: center;
	border-bottom: 1px solid var(--black);
`
const RelatedBox = styled.div`
	display: grid;
	grid-template-rows: max-content 1fr;
	&:first-of-type{
		border-right: 1px solid var(--black);
		${mobile}{
			border-right: none;
		}
	}
	${mobile}{
		border-bottom: 1px solid var(--black);
	}
`
const RelatedTiles = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
`
const RelatedArticle = styled(TileArticle)`
	&:first-of-type{
		border-right: 1px solid var(--black);
	}
`
const RelatedProduct = styled(TileProduct)`
	&:first-of-type{
		border-right: 1px solid var(--black);
	}
`
const JNBox = styled.div`
	display: grid;
	align-self: center;
	justify-self: center;
`
const JNLogo = styled(JNLetters)`
	height: 20px;
`

Article.propTypes = {
	data: PropTypes.object,
}
ShareLinksBlock.propTypes = {
	className: PropTypes.string,
	title: PropTypes.string,
}

export const query = graphql`
  query ArticleQuery($slug: String) {
    sanityArticle(slug: {current: {eq: $slug}}) {
      title
			datePublished(formatString: "DD.MM.YY")
      content {
				headerImage {
					...imageWithAlt
					asset {
            metadata {
              dimensions {
                aspectRatio
              }
            }
          }
				}
        text: _rawText(resolveReferences: {maxDepth: 6})
				seo {
					...seo
				}
				topics {
					_type
					title
					slug {
						current
					}
				}
				relatedArticles {
					...articleThumb
				}
				relatedProducts {
					...variantThumb
				}
      }
    }
  }
`

export default Article